/**
 * RatingPageNew — spec §5.3 with InlineSlider, park grouping, progress bars, compact rows.
 */
import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUpdateAttractionMetadata } from '../hooks/useUpdateAttractionMetadata';
import { useParks } from '../hooks/useParks';
import { useDebouncedCallback } from '../hooks/useDebouncedCallback';
import { DataCardRow } from '../design-system/primitives/DataCardRow';
import { SearchBar } from '../design-system/primitives/SearchBar';
import { EntityTypePill } from '../design-system/primitives/EntityTypePill';
import { EmptyState } from '../design-system/primitives/EmptyState';
import { InlineDropdown } from '../components/shared/InlineDropdown';
import { SlidersHorizontal, ChevronDown } from 'lucide-react';
import { AdminAttraction } from '../hooks/useAdminAttractions';
import { getParkIcon } from '../config/parkIcons';

/** Width-normalized InlineSlider — each row gets the same visual track length. */
function RankSlider({
  value,
  max = 10,
  onChange,
  onSave,
  onSlidingStart,
  onSlidingEnd,
  ariaLabel,
}: {
  value: number;
  max?: number;
  onChange: (v: number) => void;
  onSave?: () => void;
  onSlidingStart?: () => void;
  onSlidingEnd?: () => void;
  ariaLabel?: string;
}) {
  return (
    <div className="flex items-center gap-2 min-w-[300px] w-[300px] flex-shrink-0">
      <input
        type="range" min="1" max={max} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerDown={onSlidingStart}
        onPointerUp={(e) => {
          e.stopPropagation();
          if (onSave) onSave();
          if (onSlidingEnd) onSlidingEnd();
        }}
        className="flex-1 min-w-0 h-1 accent-disney-gold-500 cursor-pointer"
        aria-label={ariaLabel} aria-valuemin={1} aria-valuemax={max} aria-valuenow={value}
      />
      <span className="text-xs font-mono w-6 text-right text-gray-500 dark:text-gray-400 flex-shrink-0">{value}</span>
    </div>
  );
}

/** Compact priority dropdown — HIGH / MEDIUM / LOW with color-coded bg. */
function PriorityDropdown({
  attractionId,
  current,
  onSet,
}: {
  attractionId: string;
  current: string;
  onSet: (v: string) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const colorClasses: Record<string, string> = {
    HIGH: 'bg-red-300 text-red-900 dark:bg-red-900/60 dark:text-red-200',
    MEDIUM: 'bg-gray-300 text-gray-700 dark:bg-gray-600 dark:text-gray-200',
    LOW: 'bg-yellow-200 text-amber-900 dark:bg-yellow-900/50 dark:text-yellow-200',
  };
  const colorClass = colorClasses[current] ?? colorClasses.MEDIUM;

  React.useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium cursor-pointer select-none transition-colors ${colorClass} ${open ? 'ring-2 ring-offset-1 ring-disney-blue-500' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{current}</span>
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 bg-white dark:bg-surface-dark-card border border-gray-200 dark:border-gray-700 rounded shadow-lg z-50 py-1 min-w-[90px]">
          {(['HIGH', 'MEDIUM', 'LOW'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => { onSet(opt); setOpen(false); }}
              className={`w-full text-left px-3 py-1 text-xs cursor-pointer select-none transition-colors ${opt === current
                ? 'bg-disney-blue-500 text-white font-semibold'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              role="option"
              aria-selected={opt === current}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function RatingPageNew(): React.ReactElement {
  const update = useUpdateAttractionMetadata();
  const queryClient = useQueryClient();
  const { data: parks } = useParks();
  const parkList = React.useMemo(() => parks?.map(p => p.name) ?? [], [parks]);

  /* Read attractions from React Query cache — same key that useMutation's onMutate writes to.
     This ensures optimistic updates trigger a re-render so the slider value moves immediately. */
  const { data: cachedAttractions = [] } = useQuery({
    queryKey: ['admin-attractions'],
    queryFn: async () => {
      const res = await fetch('/api/admin/attractions');
      if (!res.ok) return [];
      return (await res.json()) as AdminAttraction[];
    },
  });

  // Maintain a master set of all entity types independently of filters
  const [allEntityTypes, setAllEntityTypes] = React.useState<Set<string>>(new Set());

  const entityTypeOptions = React.useMemo(() => {
    const types = allEntityTypes.size > 0 ? Array.from(allEntityTypes).sort() : ['ATTRACTION', 'SHOW'];
    return [{ value: '', label: 'All types' }, ...types.map((t) => ({ value: t, label: t }))];
  }, [allEntityTypes]);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [parkFilter, setParkFilter] = React.useState('');
  const [entityTypeFilter, setEntityTypeFilter] = React.useState('');

  // Client-side entityType + park filters (backend doesn't support them)
  const rows = React.useMemo(() => {
    const base = cachedAttractions;
    const filtered = entityTypeFilter
      ? base.filter((a) => a.entityType === entityTypeFilter)
      : base;
    const withParkFilter = parkFilter
      ? filtered.filter((a) => a.parkName === parkFilter)
      : filtered;
    if (!searchQuery) return withParkFilter;
    return withParkFilter.filter((a) => a.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [cachedAttractions, entityTypeFilter, parkFilter, searchQuery]);

  const total = cachedAttractions.length;
  const ranked = rows.filter((a: any) => a.rating).length;
  const progressPct = total > 0 ? Math.round((ranked / total) * 100) : 0;

  /* Stabilize the mutation function so the debounced callback's ref stays
     consistent across re-renders. Without this, the inline arrow function's
     reference changes on every render, which resets the debounce timer. */
  const mutateRef = React.useRef(update.mutate);
  mutateRef.current = update.mutate;

  /* Track whether the user is actively dragging the slider.
     This prevents the debounced save from firing during natural micro-pauses
     while sliding, and ensures it only fires after the user releases. */
  const isSlidingRef = React.useRef(false);

  const debouncedUpdate = useDebouncedCallback(
    (id: string, rating: number, priority: string) => {
      if (isSlidingRef.current) return;
      mutateRef.current({ id, rating, priority });
    },
    300
  );

  /* Helper for immediate optimistic cache update (called synchronously on slider change). */
  const updateCacheOptimistically = React.useCallback(
    (id: string, rating: number, priority: string) => {
      queryClient.setQueryData(['admin-attractions'], (old: any) => {
        if (!old) return old;
        return old.map((a: any) =>
          a.id === id ? { ...a, rating, priority } : a
        );
      });
    },
    [queryClient]
  );

  /* Helper to read the current rating from the cache (used by onSave to always
     get the freshest value, not stale closure data). */
  const getCurrentRating = React.useCallback(
    (id: string): number | undefined => {
      const data = queryClient.getQueryData<unknown[]>(['admin-attractions']);
      const entry = data?.find((a: unknown) => (a as { id?: string }).id === id) as { rating?: number } | undefined;
      return entry?.rating;
    },
    [queryClient]
  );

  /* Use the same data source that the mutation's onMutate writes to, so the slider reflects the optimistic update immediately. */

  /* Group by park */
  const grouped = React.useMemo(() => {
    const groups: Record<string, any[]> = {};
    rows.forEach((a) => {
      const park = a.parkName || 'Other';
      (groups[park] = groups[park] || []).push(a);
    });
    return groups;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Attraction Ranking</h1>
        <span className="text-sm text-gray-500 dark:text-gray-400">{ranked}/{total} ranked</span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div className="bg-disney-gold-500 h-2 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Controls */}
      <div className="space-y-3">
        {/* Search */}
        <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search attractions..." onClear={() => setSearchQuery('')} />

        {/* Filters row */}
        <div className="flex flex-wrap gap-2 items-center">
          <InlineDropdown
            value={parkFilter}
            onChange={setParkFilter}
            placeholder="All parks"
            options={[
              { value: '', label: 'All parks', icon: React.createElement(getParkIcon('Unknown')) },
              ...parkList.map((p) => ({ value: p, label: p, icon: React.createElement(getParkIcon(p)) })),
            ]}
          />
          <InlineDropdown
            value={entityTypeFilter}
            onChange={setEntityTypeFilter}
            placeholder="All types"
            options={entityTypeOptions}
          />
        </div>
      </div>

      {/* Park-grouped attraction rows */}
      {Object.keys(grouped).length === 0 && cachedAttractions.length === 0 ? (
        <EmptyState icon={SlidersHorizontal} iconColor="text-disney-gold-400" title="No attractions found" />
      ) : (
        Object.entries(grouped).map(([park, attrs]) => {
          const parkRanked = attrs.filter((a: any) => a.rating).length;
          const parkTotal = attrs.length;
          const parkPct = parkTotal > 0 ? Math.round((parkRanked / parkTotal) * 100) : 0;
          return (
            <div key={park}>
              <div className="flex items-center gap-2 mb-2">
                <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {React.createElement(getParkIcon(park))}{' '}{park}
                </h2>
                <span className="text-xs text-gray-400">{parkPct}%</span>
                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div className="bg-disney-blue-500 h-1.5 rounded-full" style={{ width: `${parkPct}%` }} />
                </div>
              </div>
              <div className="space-y-1">
                {attrs.map((a) => (
                  <div key={a.id} className="bg-white dark:bg-surface-dark-card rounded-lg p-3 shadow-sm grid grid-cols-[200px_300px_64px_96px] items-center gap-3 [&>div]:min-w-0">
                    <DataCardRow label={a.name} value="" />
                    <RankSlider
                      value={a.rating || 1} max={10}
                      onChange={(v) => {
                        updateCacheOptimistically(a.id, v, a.priority || 'MEDIUM');
                      }}
                      onSave={() => {
                        const rating = getCurrentRating(a.id);
                        if (rating !== undefined) {
                          debouncedUpdate(a.id, rating, a.priority || 'MEDIUM');
                        }
                      }}
                      onSlidingStart={() => { isSlidingRef.current = true; }}
                      onSlidingEnd={() => { isSlidingRef.current = false; }}
                      ariaLabel={`Rank ${a.name}`}
                    />
                    <EntityTypePill entityType={a.entityType || 'UNKNOWN'} />
                    <PriorityDropdown
                      attractionId={a.id}
                      current={a.priority || 'MEDIUM'}
                      onSet={(v) => update.mutate({ id: a.id, rating: a.rating || 1, priority: v })}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
